import React, { useCallback, useEffect, useState } from 'react';
import { Gruppe, LeaderboardEntry } from '../types';
import { ApiService } from '../api';
import Icon from './Icon';

interface LeaderboardProps {
  gruppe: Gruppe;
  onBack: () => void;
}

const Leaderboard: React.FC<LeaderboardProps> = ({ gruppe }) => {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ApiService.getLeaderboard(gruppe.id);
      const sorted = [...data].sort((a, b) => a.punkte - b.punkte);
      setLeaderboard(sorted);
    } catch (e) {
      console.error(e);
      setError('Leaderboard konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [gruppe.id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="loading-ios">
        <div className="spinner" />
        <div className="footnote">Lade Leaderboard…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="empty-ios">
        <div className="empty-emoji">😬</div>
        <div className="empty-title">Fehler</div>
        <div className="footnote" style={{ marginBottom: 14 }}>{error}</div>
        <button className="btn btn-primary" onClick={load}>Erneut versuchen</button>
      </div>
    );
  }

  if (leaderboard.length === 0) {
    return (
      <div className="empty-ios">
        <div className="empty-emoji">🏆</div>
        <div className="empty-title">Noch keine Statistik</div>
        <div className="footnote">Spielt ein paar Runden, um das Leaderboard zu füllen.</div>
      </div>
    );
  }

  const top3 = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3);

  return (
    <>
      <div className="large-title-row">
        <h1 className="large-title gradient-text">Leaderboard</h1>
        <button className="btn btn-glass btn-sm btn-icon" onClick={load} aria-label="Aktualisieren">
          <Icon name="refresh" size={18} />
        </button>
      </div>

      {top3.length === 3 ? (
        <div className="podium">
          <div className="podium-step silver">
            <div className="podium-rank">#2</div>
            <div className="podium-name">{top3[1].name}</div>
            <div className="podium-score">{top3[1].punkte}</div>
          </div>
          <div className="podium-step gold">
            <div className="podium-crown">👑</div>
            <div className="podium-rank">#1</div>
            <div className="podium-name">{top3[0].name}</div>
            <div className="podium-score">{top3[0].punkte}</div>
          </div>
          <div className="podium-step bronze">
            <div className="podium-rank">#3</div>
            <div className="podium-name">{top3[2].name}</div>
            <div className="podium-score">{top3[2].punkte}</div>
          </div>
        </div>
      ) : (
        <div className="ios-list" style={{ marginTop: 8 }}>
          {top3.map((entry, idx) => (
            <div key={entry.spieler_id} className="ios-row">
              <div className="row-icon warning">
                <Icon name="trophy" size={16} />
              </div>
              <div className="row-main">
                <div className="row-title">#{idx + 1} {entry.name}</div>
              </div>
              <div className="row-value">{entry.punkte}</div>
            </div>
          ))}
        </div>
      )}

      {rest.length > 0 && (
        <>
          <div className="section-header">Weitere Plätze</div>
          <div className="ios-list">
            {rest.map((entry, idx) => (
              <div key={entry.spieler_id} className="ios-row">
                <div className="row-icon" style={{ background: 'var(--ios-fill)', color: 'var(--ios-label)' }}>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>{idx + 4}</span>
                </div>
                <div className="row-main">
                  <div className="row-title">{entry.name}</div>
                </div>
                <div className="row-value">{entry.punkte}</div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="section-footer" style={{ textAlign: 'center', marginTop: 20 }}>
        Wenig Punkte = Gut gespielt. Der niedrigste Punktestand gewinnt.
      </div>
    </>
  );
};

export default Leaderboard;
